import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable Data Table component with sorting
 */
const DataTable = ({ 
  columns, 
  data, 
  onRowClick,
  className = 'data-table',
  emptyMessage = 'No data available'
}) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    const column = columns.find(col => col.key === sortColumn);
    if (!column || !column.sortable) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle custom sort function
      if (column.sortFn) {
        return sortDirection === 'asc' 
          ? column.sortFn(a, b)
          : column.sortFn(b, a);
      }

      // Handle null/undefined values
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Default comparison
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortDirection, columns]);

  if (data.length === 0) {
    return (
      <div className="empty-state">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <table className={className}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th 
              key={column.key}
              style={{
                cursor: column.sortable ? 'pointer' : 'default',
                userSelect: 'none',
                ...column.headerStyle,
              }}
              onClick={() => column.sortable && handleSort(column.key)}
            >
              {column.label}
              {column.sortable && sortColumn === column.key && (
                <span style={{ marginLeft: '0.25rem' }}>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((row, index) => (
          <tr 
            key={row.id || index}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{ cursor: onRowClick ? 'pointer' : 'default' }}
          >
            {columns.map((column) => (
              <td key={column.key} style={column.cellStyle}>
                {column.render 
                  ? column.render(row[column.key], row) 
                  : row[column.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      sortFn: PropTypes.func,
      headerStyle: PropTypes.object,
      cellStyle: PropTypes.object,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
  emptyMessage: PropTypes.string,
};

export default DataTable;
